import {
  Box,
  Button,
  TextField,
  Typography,
  Backdrop,
  Modal,
  Stack,
  Alert,
  Snackbar,
} from '@mui/material';
import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import theme from '../../theme';
import { useAuth } from '../../hooks/useAuth';
import { useProductData } from '../../hooks/useProductData';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LinkIcon from '@mui/icons-material/Link';
import { toast } from 'react-toastify';
import LoadingPage from '../LoadingPage';
import { createMagicLink, type MagicLinkResponse } from '../../utils/shopify';

const MAX_FILE_BYTES = 15 * 1024 * 1024; // 15 MB
const ALLOWED_FORMATS = new Set(['image/jpeg', 'image/jpg', 'image/png']);

interface MagicLinkResult {
  success: boolean;
  magicLink?: MagicLinkResponse;
  error?: string;
}

const EditProduct = () => {
  const { authUser, authLoading } = useAuth();

  const {
    productInfo,
    setProductInfo,
    handlePublishProduct,
    handleUpdateProduct,
    loadProductById,
  } = useProductData();

  const initialProductRef = useRef(productInfo);

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savingStep, setSavingStep] = useState<
    'publishing' | 'generating_link'
  >('publishing');

  const [tempImage, setTempImage] = useState<string>(
    productInfo?.imageURL ?? ''
  );
  const [cnfImage, setCnfImage] = useState<string>(productInfo?.imageURL ?? '');

  const [imgErr, setImgErr] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loadingById, setLoadingById] = useState(false);

  const [errors, setErrors] = useState<{ name?: string; description?: string }>(
    {}
  );

  // Magic Link states
  const [magicLinkResult, setMagicLinkResult] =
    useState<MagicLinkResult | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const descRef = useRef<HTMLInputElement | null>(null);

  const [searchParams] = useSearchParams();
  const productId = searchParams.get('product') || null;

  const isDirty = useMemo(() => {
    return (
      JSON.stringify(productInfo) !== JSON.stringify(initialProductRef.current)
    );
  }, [productInfo]);

  const navigate = useNavigate();

  // Auth guard
  useEffect(() => {
    if (authLoading) return;
    if (!authUser) {
      navigate('/creators');
      toast.error('Login To Add Products');
      return;
    }
  }, [navigate, authLoading, authUser]);

  // Load product by id (edit mode)
  useEffect(() => {
    if (authLoading) return;
    const uid = authUser?.uid;
    if (!uid || !productId) return;
    if (productInfo?.id === productId) return;

    setLoadingById(true);
    if (productInfo) setProductInfo(null);

    loadProductById(uid, productId)
      .then((data) => {
        // update image previews from stored URL (if any)
        setTempImage(data?.imageURL ?? '');
        setCnfImage(data?.imageURL ?? '');
      })
      .catch(() => {
        toast.error('Could not load this product.');
        navigate(`/${authUser?.userName}/products`);
      })
      .finally(() => setLoadingById(false));
  }, [
    authLoading,
    authUser?.uid,
    authUser?.userName,
    productId,
    productInfo?.id,
    loadProductById,
    navigate,
    setProductInfo,
  ]);

  // Reset the dirty-state baseline whenever we have a (new) loaded product id
  useEffect(() => {
    if (productInfo?.id) {
      initialProductRef.current = productInfo;
    }
  }, [productInfo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revoke confirmed blob URL when it changes/unmounts
  useEffect(() => {
    return () => {
      if (cnfImage?.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(cnfImage);
        } catch {}
      }
    };
  }, [cnfImage]);

  // Revoke temp blob on unmount
  useEffect(() => {
    return () => {
      if (tempImage?.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(tempImage);
        } catch {}
      }
    };
  }, []); // only on unmount

  const validate = useCallback(() => {
    const e: { name?: string; description?: string } = {};
    if (!productInfo?.name?.trim()) e.name = 'Product Title is required';
    if (!productInfo?.description?.trim())
      e.description = 'Product Description is required';
    setErrors(e);

    if (e.name || e.description) {
      setTimeout(() => {
        if (e.name) nameRef.current?.focus();
        else if (e.description) descRef.current?.focus();
      }, 0);
      return false;
    }
    return true;
  }, [productInfo]);

  const update = <K extends keyof NonNullable<typeof productInfo>>(
    key: K,
    val: NonNullable<typeof productInfo>[K]
  ) => {
    if (!productInfo) return;
    setProductInfo({ ...productInfo, [key]: val });

    if (key === 'name' && errors.name && String(val ?? '').trim()) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
    if (
      key === 'description' &&
      errors.description &&
      String(val ?? '').trim()
    ) {
      setErrors((prev) => ({ ...prev, description: undefined }));
    }
  };

  const handleCancel = () => {
    setProductInfo(null);
    navigate(`/${authUser?.userName}/products`);
  };

  /**
   * Generate Magic Link for the product
   * @param productUrl - The original product URL (Shopify URL)
   * @param productName - The product name
   * @param userProductId - The ID from our user_products collection (NOT Shopify product ID)
   */
  const generateMagicLinkForProduct = async (
    productUrl: string,
    productName?: string,
    userProductId?: string
  ): Promise<MagicLinkResult> => {
    if (!authUser?.uid) {
      return { success: false, error: 'User not authenticated' };
    }

    // IMPORTANT: userProductId should be the ID from our database, not Shopify's product ID
    if (!userProductId) {
      console.error('generateMagicLinkForProduct called without userProductId');
      return {
        success: false,
        error: 'Product ID is required to generate Magic Link',
      };
    }

    console.log('Generating Magic Link with:', {
      product_url: productUrl,
      persona_id: authUser.uid,
      product_name: productName,
      product_id: userProductId, // This is our user_products ID
    });

    try {
      const magicLink = await createMagicLink({
        product_url: productUrl,
        persona_id: authUser.uid,
        product_name: productName,
        product_id: userProductId, // Pass the user_products ID, not Shopify ID
        metadata: {
          source: 'product_page',
          created_via: 'publish_flow',
        },
      });

      return { success: true, magicLink };
    } catch (err: any) {
      console.error('Failed to generate Magic Link:', err);

      // Check if it's a configuration error (no affiliate ID)
      if (err.message?.includes('affiliate_link_id')) {
        return {
          success: false,
          error:
            'Shopify Collabs not configured. Please set up your affiliate link in settings.',
        };
      }

      return {
        success: false,
        error: err.message || 'Failed to generate Magic Link',
      };
    }
  };

  const handleCopyLink = async () => {
    if (magicLinkResult?.magicLink?.tracking_url) {
      try {
        await navigator.clipboard.writeText(
          magicLinkResult.magicLink.tracking_url
        );
        setCopied(true);
        setSnackbar({
          open: true,
          message: 'Magic Link copied to clipboard!',
          severity: 'success',
        });
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        setSnackbar({
          open: true,
          message: 'Failed to copy link',
          severity: 'error',
        });
      }
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setMagicLinkResult(null);
    setCopied(false);
    navigate(`/${authUser?.userName}/products`);
  };

  const publishProductHandler = async () => {
    if (!validate()) return;
    setIsSaving(true);
    setSavingStep('publishing');

    const isNewProduct = !productInfo?.id;

    try {
      if (isNewProduct) {
        // New product: Publish first, then generate Magic Link
        // IMPORTANT: handlePublishProduct should return the created product data including the ID
        const publishedProduct = await handlePublishProduct(
          authUser?.uid || ''
        );

        // Get the newly created product ID from the response
        // The ID should be returned from handlePublishProduct
        const newProductId = publishedProduct?.id || publishedProduct?.docId;

        if (!newProductId) {
          console.error(
            'handlePublishProduct did not return a product ID',
            publishedProduct
          );
          toast.error('Product was created but could not retrieve product ID');
          setIsSaving(false);
          navigate(`/${authUser?.userName}/products`);
          return;
        }

        console.log('Product published with ID:', newProductId);

        // Step 2: Generate Magic Link with the NEW product ID
        setSavingStep('generating_link');

        const linkResult = await generateMagicLinkForProduct(
          productInfo?.productURL || '',
          productInfo?.name,
          newProductId // Use the ID returned from handlePublishProduct
        );

        setMagicLinkResult(linkResult);
        setIsSaving(false);

        if (linkResult.success) {
          // Show success modal with Magic Link
          setShowSuccessModal(true);
        } else {
          // Magic Link failed but product was published - show warning and navigate
          setSnackbar({
            open: true,
            message: `Product published, but Magic Link generation failed: ${linkResult.error}`,
            severity: 'warning',
          });
          navigate(`/${authUser?.userName}/products`);
        }
      } else {
        // Existing product: Just update, no Magic Link generation
        await handleUpdateProduct(authUser?.uid || '');
        setIsSaving(false);
        navigate(`/${authUser?.userName}/products`);
      }
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong.');
      setIsSaving(false);
    }
  };

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_FORMATS.has(file.type)) {
      return 'Only JPG and PNG files are allowed.';
    }
    if (file.size > MAX_FILE_BYTES) {
      return 'File is too large. Maximum size allowed is 15 MB.';
    }
    return null;
  };

  const openImageModal = () => {
    setTempImage(cnfImage || productInfo?.imageURL || '');
    setImgErr(null);
    setSelectedFile(null);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setImgErr(null);
  };

  const handleSaveImage = () => {
    if (!tempImage || !selectedFile) {
      setImgErr('Please select an image');
      return;
    }
    if (!tempImage && !selectedFile && imgErr) return;

    // Clean up any prior confirmed blob
    if (cnfImage?.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(cnfImage);
      } catch {}
    }

    setProductInfo({
      ...productInfo!,
      image: selectedFile,
      imageURL: null,
    });

    const url = URL.createObjectURL(selectedFile);
    setCnfImage(url);
    closeImageModal();
  };

  const handleFileBrowse = () => fileInputRef.current?.click();

  const handleValidFile = (file: File) => {
    setImgErr(null);
    setSelectedFile(file);

    if (tempImage?.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(tempImage);
      } catch {}
    }
    const url = URL.createObjectURL(file);
    setTempImage(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) {
      setImgErr(err);
      return;
    }
    handleValidFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) {
      setImgErr(err);
      setSelectedFile(null);
      return;
    }
    handleValidFile(file);
  };

  const hasImgError = Boolean(imgErr);

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    margin: 'auto',
    maxWidth: '550px',
    minWidth: '400px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '10px',
    p: 4,
  };

  if (!productInfo && !productId) return null;
  if (productId && loadingById) return <LoadingPage />;

  return (
    <Box>
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h7'>Edit Product</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          <Button
            variant='contained'
            sx={{
              border: `${theme.palette.primary._states.outlinedBorder}!important`,
              bgcolor: `${theme.palette.secondary.main} !important`,
              color: `${theme.palette.secondary.contrastText} !important`,
            }}
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            sx={{
              bgcolor: `${theme.palette.primary.main}`,
              color: `${theme.palette.primary.contrastText}`,
            }}
            onClick={publishProductHandler}
            disabled={isSaving || (!!productInfo?.id && !isDirty)}
          >
            {productInfo?.id ? 'Save' : 'Publish'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            pb: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant='h7'>Product Details</Typography>
          <Typography variant='body2'>
            This information will be used to recommend your product in chats and
            replies.
          </Typography>
        </Box>

        <Box>
          <Typography variant='h8'>Product Link</Typography>
          {!productId && (
            <Typography variant='body2' sx={{ color: theme.palette.info.main }}>
              Information retrieved from the provided link/URL may be incomplete
              or subject to omissions.
            </Typography>
          )}
          <TextField
            fullWidth
            value={productInfo?.productURL}
            disabled
            slotProps={{
              input: {
                sx: {
                  bgcolor: theme.palette.action.disabledBackground,
                  color: 'text.primary',
                  height: '100%',
                  borderRadius: 2,
                  '& fieldset': {
                    border: `1px solid ${theme.palette['_components'].input.standard.enabledBorder}`,
                  },
                },
              },
            }}
            variant='outlined'
            sx={{
              mt: 1,
              height: '40px',
              flex: 1,
              '& .MuiInputBase-root': { height: '100%' },
            }}
          />
        </Box>

        <Box>
          <Typography variant='h8'>
            Product Image
            <Typography component='span' color='error'>
              *
            </Typography>
          </Typography>
          <Box
            sx={{
              position: 'relative',
              p: 1,
              borderRadius: 3,
              overflow: 'hidden',
              width: { xs: '100%', sm: 220, md: 280 },
              aspectRatio: '16 / 9',
              border: `2px solid ${
                theme.palette.mode === 'dark'
                  ? theme.palette['_components'].input.filled.hoverFill
                  : theme.palette['_components'].input.standard.enabledBorder
              }`,
              mt: 1,
              '&:hover .edit-overlay': { opacity: 1 },
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {cnfImage ? (
              <img
                src={cnfImage ?? ''}
                style={{
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 6,
                  display: 'block',
                }}
                alt={productInfo?.name || 'Product image'}
              />
            ) : (
              <Typography>Add Thumbnail</Typography>
            )}

            {/* Hover overlay */}
            <Box
              className='edit-overlay'
              onClick={openImageModal}
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.45)',
                color: '#fff',
                opacity: 0,
                transition: 'opacity .2s ease',
                fontWeight: 600,
              }}
            >
              <Typography variant='body2'>Edit image</Typography>
            </Box>
          </Box>
        </Box>

        <Box>
          <Typography variant='h8'>
            Product Title
            <Typography component='span' color='error'>
              *
            </Typography>
          </Typography>
          <TextField
            fullWidth
            multiline
            value={productInfo?.name ?? ''}
            onChange={(e) => update('name', e.target.value)}
            slotProps={{
              input: {
                sx: {
                  color: 'text.primary',
                  height: '100%',
                  borderRadius: 2,
                  '& fieldset': { borderWidth: '2px' },
                },
              },
            }}
            variant='outlined'
            sx={{
              mt: 1,
              minHeight: '40px',
              flex: 1,
              '& .MuiInputBase-root': { height: '100%' },
            }}
            error={!!errors.name}
            helperText={errors.name}
            inputRef={nameRef}
          />
        </Box>

        <Box>
          <Box>
            <Typography variant='h8'>
              Description
              <Typography component='span' color='error'>
                *
              </Typography>
            </Typography>
            <Typography variant='body2'>
              Be as descriptive as possible, to help AI better pitch the
              product!
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            value={productInfo?.description ?? ''}
            onChange={(e) => update('description', e.target.value)}
            slotProps={{
              input: {
                sx: {
                  color: 'text.primary',
                  height: '100%',
                  borderRadius: 2,
                  '& fieldset': { borderWidth: '2px' },
                },
              },
            }}
            variant='outlined'
            sx={{
              mt: 1,
              minHeight: '40px',
              flex: 1,
              '& .MuiInputBase-root': { height: '100%' },
            }}
            error={!!errors.description}
            helperText={errors.description}
            inputRef={descRef}
          />
        </Box>
      </Box>

      {/* Image Edit Modal */}
      <Modal
        open={imageModalOpen}
        onClose={closeImageModal}
        keepMounted
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: 800,
            width: '90vw',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 3,
            p: 3,
          }}
        >
          <Typography variant='h6' gutterBottom>
            Update product image
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            {/* Live preview */}
            <Box>
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  maxHeight: { xs: 300, md: 250 },
                  border: `1px solid ${
                    hasImgError
                      ? theme.palette.error.main
                      : theme.palette.divider
                  }`,
                  aspectRatio: '16 / 9',
                  bgcolor: theme.palette.background.default,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {tempImage ? (
                  <img
                    src={tempImage}
                    alt='Preview'
                    style={{
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    No image selected
                  </Box>
                )}
              </Box>
              {!!imgErr && (
                <Typography
                  variant='caption'
                  color='error'
                  sx={{ mt: 1, display: 'block' }}
                >
                  {imgErr}
                </Typography>
              )}
            </Box>

            {/* Dropzone / Browse */}
            <Box
              sx={{
                border: '2px dashed',
                borderColor: hasImgError ? 'error.main' : '#42424280',
                bgcolor: (t) =>
                  t.palette.background['paper-elevation-8'] ??
                  t.palette.background.paper,
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <Typography
                variant='body1'
                color='text.primary'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <UploadFileIcon fontSize='small' />
                Drop JPG/PNG
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                or
              </Typography>

              <Stack direction='row' spacing={1} justifyContent='center'>
                <Button
                  variant='outlined'
                  onClick={handleFileBrowse}
                  startIcon={<UploadFileIcon />}
                >
                  Browse Files
                </Button>
              </Stack>
              {/* hidden file input */}
              <input
                type='file'
                accept='.jpg,.jpeg,.png'
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Box>
          </Box>

          <Stack
            direction='row'
            spacing={1}
            justifyContent='flex-end'
            sx={{ mt: 3 }}
          >
            <Button onClick={closeImageModal}>Cancel</Button>
            <Button
              variant='contained'
              onClick={handleSaveImage}
              disabled={!tempImage || !selectedFile}
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Saving/Publishing Modal */}
      <Modal
        open={isSaving}
        keepMounted
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            margin: 'auto',
            maxWidth: { xs: '90dvw', sm: '500px' },
            minWidth: { xs: '80dvw', sm: '400px' },
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: '10px',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <LoadingPage />
          <Typography variant='h8'>
            {productInfo?.id
              ? 'Saving...'
              : savingStep === 'publishing'
              ? 'Publishing...'
              : 'Generating Magic Link...'}
          </Typography>
          {!productInfo?.id && (
            <Typography variant='body2' color='text.secondary'>
              {savingStep === 'publishing'
                ? 'Please wait, saving product details...'
                : 'Creating your trackable affiliate link...'}
            </Typography>
          )}

          {/* Progress indicator for new products */}
          {!productInfo?.id && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mt: 2,
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor:
                    savingStep === 'publishing'
                      ? theme.palette.primary.main
                      : theme.palette.success.main,
                }}
              />
              <Typography variant='caption' color='text.secondary'>
                Publish Product
              </Typography>
              <Box
                sx={{
                  width: 20,
                  height: 2,
                  bgcolor: theme.palette.divider,
                }}
              />
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor:
                    savingStep === 'generating_link'
                      ? theme.palette.primary.main
                      : theme.palette.grey[300],
                }}
              />
              <Typography variant='caption' color='text.secondary'>
                Generate Magic Link
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Success Modal with Magic Link */}
      <Modal
        open={showSuccessModal}
        onClose={handleCloseSuccessModal}
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: 32,
                  color: theme.palette.success.main,
                }}
              />
              <Box>
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                  Product Published Successfully!
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Your Magic Link is ready to share
                </Typography>
              </Box>
            </Box>

            {/* Product Info */}
            {magicLinkResult?.magicLink && (
              <Box
                sx={{
                  p: 2,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[900]
                      : theme.palette.grey[50],
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1 }}>
                  {magicLinkResult.magicLink.product_name}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <Typography variant='caption' color='text.secondary'>
                    Product ID: {magicLinkResult.magicLink.product_id}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Affiliate ID: {magicLinkResult.magicLink.affiliate_link_id}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Magic Link Display */}
            {magicLinkResult?.magicLink && (
              <Box>
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <LinkIcon fontSize='small' />
                  Your Magic Link
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <TextField
                    fullWidth
                    value={magicLinkResult.magicLink.tracking_url}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        fontSize: 13,
                        bgcolor: theme.palette.background.paper,
                      },
                    }}
                    size='small'
                  />
                  <Button
                    variant={copied ? 'contained' : 'outlined'}
                    color={copied ? 'success' : 'primary'}
                    onClick={handleCopyLink}
                    sx={{
                      minWidth: 100,
                      height: 40,
                    }}
                    startIcon={
                      copied ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />
                    }
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </Box>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ mt: 1, display: 'block' }}
                >
                  Share this link to track clicks and earn commissions
                </Typography>
              </Box>
            )}

            {/* Error display */}
            {magicLinkResult?.error && (
              <Alert severity='warning' sx={{ mt: 1 }}>
                {magicLinkResult.error}
              </Alert>
            )}

            {/* Actions */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mt: 1,
              }}
            >
              <Button
                variant='contained'
                onClick={handleCloseSuccessModal}
                fullWidth
              >
                Done
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditProduct;
